import { Controller } from "@hotwired/stimulus"
import { apiPost, t } from "../api_helper"
import { networkStatus } from "../lib/network_status"
import { alertOffline } from "../lib/offline_ui"
import "../lib/paywall"

export default class extends Controller {
  static targets = ["dropZone", "fileInput", "previewSection", "previewList", "itemCount", "confirmBtn", "successOverlay", "successText", "uploadSection", "selectAllBtn"]

  connect() {
    this.transactions = []
    this.selected = new Set()
  }

  triggerFileInput() {
    this.fileInputTarget.click()
  }

  onFileSelected(event) {
    const file = event.target.files[0]
    if (file) this.uploadFile(file)
  }

  onDragOver(event) {
    event.preventDefault()
    this.dropZoneTarget.style.borderColor = "rgb(99,102,241)"
    this.dropZoneTarget.style.backgroundColor = "rgb(248,250,252)"
  }

  onDragLeave(event) {
    event.preventDefault()
    this.dropZoneTarget.style.borderColor = "rgb(148,163,184)"
    this.dropZoneTarget.style.backgroundColor = "transparent"
  }

  onDrop(event) {
    event.preventDefault()
    this.dropZoneTarget.style.borderColor = "rgb(148,163,184)"
    this.dropZoneTarget.style.backgroundColor = "transparent"

    const file = event.dataTransfer.files[0]
    if (file) this.uploadFile(file)
  }

  async uploadFile(file) {
    if (!networkStatus.online) { alertOffline(); return }
    const formData = new FormData()
    formData.append("file", file)

    const csrfMeta = document.querySelector('meta[name="csrf-token"]')
    const csrfToken = csrfMeta ? csrfMeta.getAttribute("content") : ""

    try {
      this.dropZoneTarget.style.opacity = "0.5"

      const response = await fetch("/api/import/preview", {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken
        },
        credentials: "same-origin",
        body: formData
      })

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        if (response.status === 401) {
          window.location.href = "/pwa/login"
          return
        }
        if (response.status === 403) {
          window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "import" } }))
          return
        }
        alert(data.error || `${t("import.error") || "Import error"}: ${response.status}`)
        return
      }

      const data = await response.json()

      if (data.error && (!data.transactions || data.transactions.length === 0)) {
        alert(data.error)
        return
      }

      this.transactions = data.transactions || []
      this.selected = new Set(this.transactions.map((_, i) => i))
      this.renderPreview()
    } catch (err) {
      alert(`${t("import.error") || "Import error"}: ${err.message}`)
    } finally {
      this.dropZoneTarget.style.opacity = "1"
    }
  }

  renderPreview() {
    if (this.transactions.length === 0) {
      this.previewSectionTarget.classList.add("hidden")
      this.uploadSectionTarget.classList.remove("hidden")
      return
    }

    this.uploadSectionTarget.classList.add("hidden")
    this.previewSectionTarget.classList.remove("hidden")

    const selectedCount = this.selected.size
    const totalCount = this.transactions.length
    this.itemCountTarget.textContent = `${selectedCount} / ${totalCount}`

    this.updateSelectAllBtn()
    this.updateConfirmBtn()

    const list = this.previewListTarget
    list.innerHTML = ""

    this.transactions.forEach((tx, index) => {
      const isSelected = this.selected.has(index)
      const item = document.createElement("div")
      item.className = "flex items-center gap-3 p-3 rounded-2xl mb-2"
      item.style.cssText = `background-color: rgb(248,250,252); cursor: pointer; opacity: ${isSelected ? "1" : "0.45"}; transition: opacity 0.15s;`
      item.dataset.action = "click->pwa-import#toggleItem"
      item.dataset.pwaImportIndexParam = index

      const kindIcon = tx.kind === "income" ? "+" : "-"
      const kindColor = tx.kind === "income" ? "rgb(16,185,129)" : "rgb(239,68,68)"

      const checkColor = isSelected ? "rgb(99,102,241)" : "rgb(148,163,184)"
      const checkBg = isSelected ? checkColor : "transparent"
      const checkBorder = isSelected ? checkColor : "rgb(148,163,184)"

      item.innerHTML = `
        <div class="flex-shrink-0 flex items-center justify-center" style="width: 22px; height: 22px; border-radius: 6px; border: 2px solid ${checkBorder}; background-color: ${checkBg};">
          ${isSelected ? '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>' : ""}
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center gap-2">
            <span class="font-semibold" style="color: ${kindColor};">${kindIcon}${parseFloat(tx.amount).toLocaleString()}</span>
            <span class="text-xs px-2 py-0.5 rounded-full" style="background-color: rgb(241,245,249); color: rgb(148,163,184);">${tx.category_name || tx.original_category || ""}</span>
          </div>
          <div class="text-xs mt-1 truncate" style="color: rgb(148,163,184);">
            ${tx.note || ""}${tx.date ? " \u00b7 " + tx.date : ""}${tx.currency ? " \u00b7 " + tx.currency : ""}
          </div>
        </div>
      `

      list.appendChild(item)
    })
  }

  toggleItem(event) {
    const index = parseInt(event.params.index)
    if (this.selected.has(index)) {
      this.selected.delete(index)
    } else {
      this.selected.add(index)
    }
    this.renderPreview()
  }

  toggleSelectAll() {
    if (this.selected.size === this.transactions.length) {
      this.selected.clear()
    } else {
      this.selected = new Set(this.transactions.map((_, i) => i))
    }
    this.renderPreview()
  }

  updateSelectAllBtn() {
    if (!this.hasSelectAllBtnTarget) return
    const allSelected = this.selected.size === this.transactions.length
    this.selectAllBtnTarget.textContent = allSelected
      ? (t("import.deselect_all") || "Deselect all")
      : (t("import.select_all") || "Select all")
  }

  updateConfirmBtn() {
    if (!this.hasConfirmBtnTarget) return
    const count = this.selected.size
    this.confirmBtnTarget.disabled = count === 0
    this.confirmBtnTarget.style.opacity = count === 0 ? "0.4" : "1"
    const label = (t("import.confirm_import_count") || "Import %{count}").replace("%{count}", count)
    this.confirmBtnTarget.textContent = label
  }

  async confirmImport() {
    if (!networkStatus.online) { alertOffline(); return }
    if (this.selected.size === 0) return

    this.confirmBtnTarget.disabled = true
    this.confirmBtnTarget.style.opacity = "0.5"

    const selectedTransactions = this.transactions.filter((_, i) => this.selected.has(i))

    try {
      const data = await apiPost("/api/import/confirm", { transactions: selectedTransactions })

      if (data.success) {
        this.showSuccess(data.count)
      }
    } catch (err) {
      if (err.status === 403) {
        window.dispatchEvent(new CustomEvent("show-paywall", { detail: { feature: "import" } }))
      } else {
        alert(`${t("import.error") || "Import error"}: ${err.message}`)
      }
    } finally {
      this.confirmBtnTarget.disabled = false
      this.confirmBtnTarget.style.opacity = "1"
    }
  }

  showSuccess(count) {
    this.successTextTarget.textContent = (t("import.success") || "Imported %{count} transactions").replace("%{count}", count)
    this.successOverlayTarget.classList.remove("hidden")

    setTimeout(() => {
      this.successOverlayTarget.classList.add("hidden")
      this.transactions = []
      this.selected.clear()
      this.previewSectionTarget.classList.add("hidden")
      this.uploadSectionTarget.classList.remove("hidden")
      this.fileInputTarget.value = ""
    }, 2000)
  }
}
